import {
  Button,
  DateField,
  NumberField,
  TextField,
  Typography,
} from "@hellocarbo/lasagne";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  Form,
  Link,
  redirect,
  redirectDocument,
} from "react-router";
import invariant from "tiny-invariant";

import Card from "@/components/ui/Card";
import PageWrapper from "@/components/ui/PageWrapper";
import { PortalContainerContext } from "@/contexts";
import { TSimulation } from "@/types";

import { useSimulationContext } from "./Simulation";

export async function action({ request, params }: ActionFunctionArgs) {
  const { simulationId } = params;
  invariant(simulationId, "No simulation id found.");

  const formData = await request.formData();

  const name = formData.get("name");
  const periodStart = formData.get("periodStart");
  const periodEnd = formData.get("periodEnd");
  const employees = formData.get("employees");

  const response = await fetch(`/api/client/simulations/${simulationId}`, {
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name,
      period_start: periodStart,
      period_end: periodEnd,
      employees,
    }),
    method: "PATCH",
  });

  if (response.status !== 200) {
    return { ok: false };
  }

  const { data: simulation } = (await response.json()) as { data: TSimulation };

  // If the simulation has a scenario, skip the categories page and redirect to the collect.
  // Otherwise, redirect to the categories page.
  if (simulation.scenario) {
    // Forces document reload since we are leaving the react-router app.
    return redirectDocument(simulation.details_url);
  }
  return redirect(`/simulations/${simulationId}/categories`);
}

function SimulationParameters() {
  const { simulation } = useSimulationContext();
  const { t, i18n } = useTranslation(["common", "simulation"]);
  const { container } = useContext(PortalContainerContext);

  invariant(simulation, "No simulation found!");

  return (
    <PageWrapper>
      <div className="mx-xl my-lg">
        <Typography step="3" as="h1" weight="bold" className="mb-md">
          {t("simulation:simulationParameters")}
        </Typography>
        <Card>
          <Typography as="h2" step="2" weight="bold" className="mb-md">
            {t("simulation:modifyParameters")}
          </Typography>
          <Form method="PATCH">
            <TextField
              name="name"
              label={t("simulation:parameters.simulationName")}
              defaultValue={simulation.name}
            />
            <div className="my-md gap-x-lg grid grid-cols-2">
              <DateField
                language={i18n.language as "fr" | "en"}
                name="periodStart"
                label={t("simulation:parameters.startDate")}
                defaultValue={new Date(simulation.period_start)}
                popoverProps={{
                  portalContainer: container as Element,
                  zIndex: "z-10000", // Remove this gigantic z-index when the navbar has a better z-index (currently it's 9999)
                }}
              />
              <DateField
                language={i18n.language as "fr" | "en"}
                name="periodEnd"
                label={t("simulation:parameters.endDate")}
                defaultValue={new Date(simulation.period_end)}
                popoverProps={{
                  portalContainer: container as Element,
                  zIndex: "z-10000", // Remove this gigantic z-index when the navbar has a better z-index (currently it's 9999)
                }}
              />
            </div>
            <NumberField
              name="employees"
              label={t("simulation:parameters.employeesNumber")}
              defaultValue={simulation.employees}
            />
            <div className="mt-lg gap-x-md flex items-center justify-center">
              <Button shape="link" as={Link} to="/simulations">
                {t("common:back")}
              </Button>
              <Button type="submit">{t("common:next")}</Button>
            </div>
          </Form>
        </Card>
      </div>
    </PageWrapper>
  );
}

export default SimulationParameters;
